import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import MenuCheck from '~/components/Menu/MenuCheck';

/** One entry of a choice list: its value, how it reads, and an optional swatch or glyph. */
export interface ConsoleChoice<T> {
    readonly value: T;
    readonly label: string;
    readonly icon?: ReactNode;
}

interface ConsoleChoiceItems<T> {
    readonly choices: ReadonlyArray<ConsoleChoice<T>>;
    readonly value: T;
    readonly onSelect: (value: T) => void;
    /** Closes the list; called on every click, chosen or not. */
    readonly onDone: () => void;
}

/**
 * A fixed list of choices as console menu rows, the one in force marked.
 * Choosing the entry in force changes nothing. Values are compared by
 * identity, so a choice's value must be a primitive or the very object
 * listed.
 *
 * Returned as an array of rows rather than as a component: a menu manages
 * focus by looking at its direct children, and a component in their place
 * would take the focus meant for its first row and lose it.
 */
export function consoleChoiceItems<T>({
    choices,
    value,
    onSelect,
    onDone,
}: ConsoleChoiceItems<T>): ReactElement[] {
    return choices.map((choice) => {
        const isCurrent = choice.value === value;
        return (
            <MenuItem
                key={choice.label}
                dense
                selected={isCurrent}
                onClick={() => {
                    onDone();
                    if (!isCurrent) {
                        onSelect(choice.value);
                    }
                }}
            >
                {choice.icon !== undefined ? (
                    <ListItemIcon>{choice.icon}</ListItemIcon>
                ) : null}
                <ListItemText>{choice.label}</ListItemText>
                <MenuCheck selected={isCurrent} />
            </MenuItem>
        );
    });
}
